/**
 * Storage URLs for pictures already in the generation list.
 * The list itself does not ask Storage. An open row asks, eight at a time.
 * A URL is remembered for the visit. A failure is forgotten when the row closes.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { writesStayLocal } from './localOnly';

export const PICTURE_URL_LIMIT = 8;

const urls = new Map();
const listeners = new Set();
const queued = new Set();
const queue = [];
let active = 0;
let resolver = null;

export function generationHasPicture(generation) {
  if (!generation || generation.error) return false;
  return Boolean(
    generation.output?.imageUrl
    || generation.output?.path
    || generation.output?.contentUrl
    || generation.storagePath
  );
}

export function idsNeedingPictureUrl(generations) {
  const ids = [];
  for (const generation of generations || []) {
    if (!generation?.id || !generationHasPicture(generation)) continue;
    if (generation.output?.imageUrl) continue;
    const known = urls.get(generation.id);
    if (known) continue;
    ids.push(generation.id);
  }
  return ids;
}

export function subscribePictureUrls(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function pictureUrlFor(id) {
  return urls.get(id) || '';
}

export function pictureUrlActive() {
  return active;
}

/** Test hook. Production uses attachGenerationPicture. */
export function setPictureResolver(next) {
  resolver = next;
}

export function resetPictureLoads() {
  urls.clear();
  queued.clear();
  queue.length = 0;
  active = 0;
  resolver = null;
  emit();
}

export function clearFailedPictureUrl(id) {
  if (urls.get(id) === 'failed') {
    urls.delete(id);
    emit();
  }
}

async function defaultResolve(projectId, generation) {
  const { attachGenerationPicture } = await import('./cloudGenerations');
  const next = await attachGenerationPicture(projectId, generation);
  return next?.output?.imageUrl || '';
}

function pump() {
  while (active < PICTURE_URL_LIMIT && queue.length) {
    const job = queue.shift();
    if (!job || urls.has(job.generation.id)) {
      if (job) queued.delete(job.generation.id);
      continue;
    }
    active += 1;
    const run = resolver
      ? resolver(job.projectId, job.generation)
      : defaultResolve(job.projectId, job.generation);
    Promise.resolve(run)
      .then((url) => {
        urls.set(job.generation.id, url || 'failed');
      })
      .catch(() => {
        urls.set(job.generation.id, 'failed');
      })
      .finally(() => {
        queued.delete(job.generation.id);
        active = Math.max(0, active - 1);
        emit();
        pump();
      });
  }
}

export function enqueuePictureUrls(projectId, generations, { local = writesStayLocal() } = {}) {
  if (local || !projectId) return;
  let added = false;
  for (const generation of generations || []) {
    if (!idsNeedingPictureUrl([generation]).length) continue;
    if (queued.has(generation.id)) continue;
    queued.add(generation.id);
    queue.push({ projectId, generation });
    added = true;
  }
  if (added) pump();
}

export function usePictureUrl(projectId, generation, enabled = true) {
  const id = generation?.id || '';
  const direct = generation?.output?.imageUrl || '';
  const generationRef = useRef(generation);
  generationRef.current = generation;
  const cached = useSyncExternalStore(
    subscribePictureUrls,
    () => (id ? urls.get(id) || '' : ''),
    () => ''
  );

  useEffect(() => {
    if (!enabled || !id || direct || writesStayLocal()) return undefined;
    enqueuePictureUrls(projectId, [generationRef.current]);
    return () => clearFailedPictureUrl(id);
  }, [projectId, id, direct, enabled]);

  if (direct) return direct;
  return cached;
}
